import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { useConfigId } from '../../contexts/ConfigContext'
import { Select } from '../../components/ui'
import ConditionBuilder, { L3_KEYS, L2_VAR_KEYS } from '../../components/ConditionBuilder'
import {
  getGraphSettings,
  getLLMModels,
  updateAgent,
  updateSetting,
  getPrompts,
  updatePrompt,
  getConditionalPrompts,
  updateConditionalPrompts,
  getVariableDefinitions,
} from '../../api/graph_settings'
import { getApiErrorMessage } from '../../api'
import { AGENT_KEYS, AGENT_CONVERSATION_MESSAGES_KEYS, USER_PROMPT_PAYLOAD_SCHEMAS, MESSAGE_ANALYZER_OUTPUT_SCHEMA_AS_OBJECT, GENERATOR_OUTPUT_SCHEMAS } from '../../config/graphEntities'
import { useToast } from '../../contexts/ToastContext'
import { PageSection, SettingsBlock, SettingsField, SettingsFormWithPanel } from '../../components/ui'
import { SettingsItemCard } from '../../components/settings'
import ConfirmModal from '../../components/ConfirmModal'
import StructuredDataView from '../../components/StructuredDataView'
import '../../styles/pages/page-common.css'

export default function SettingsAgentDetail() {
  const { t } = useI18n()
  const { showToast } = useToast()
  const configId = useConfigId()
  const { agentKey } = useParams()
  const [data, setData] = useState(null)
  const [prompts, setPrompts] = useState([])
  const [conditionalRules, setConditionalRules] = useState([])
  const [agentEdit, setAgentEdit] = useState({})
  const [conversationMessagesEdit, setConversationMessagesEdit] = useState('')
  const [runEveryNEdit, setRunEveryNEdit] = useState('')
  const [baseEdit, setBaseEdit] = useState('')
  const [conditionalRulesEdit, setConditionalRulesEdit] = useState([])
  const [l4Definitions, setL4Definitions] = useState([])
  const [asyncModels, setAsyncModels] = useState([])
  const [loadingModels, setLoadingModels] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const isValid = agentKey && AGENT_KEYS.includes(agentKey)

  // Загружаем settings, prompts, conditional, variable_definitions (L4 ключи для условий)
  useEffect(() => {
    if (!agentKey) return
    Promise.all([
      getGraphSettings(configId),
      getPrompts(configId),
      getConditionalPrompts(agentKey, configId),
      getVariableDefinitions(configId),
    ])
      .then(([d, p, cp, vd]) => {
        setData(d)
        const agentPrompts = (p?.data || []).filter((x) => x.agent_key === agentKey)
        setPrompts(agentPrompts)
        const agent = d?.agents?.[agentKey] || {}
        const sysPrompt = agentPrompts.find((x) => x.role === 'system')
        const convKey = AGENT_CONVERSATION_MESSAGES_KEYS[agentKey]
        setAgentEdit(agent)
        setConversationMessagesEdit(String(d?.settings?.[convKey] ?? ''))
        setRunEveryNEdit(String(d?.settings?.memory_summary_run_every_n_messages ?? 10))
        setBaseEdit(sysPrompt?.base ?? '')
        const rules = cp?.rules || []
        setConditionalRules(rules)
        setConditionalRulesEdit(rules)
        const defs = vd?.definitions || []
        setL4Definitions(Array.isArray(defs) ? defs : [])
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [agentKey, configId])

  // Модели загружаются только при открытии Select (onOpen), не при монтировании

  async function loadModels() {
    if (loadingModels || asyncModels.length > 0) return
    setLoadingModels(true)
    try {
      const { models, providerErrors } = await getLLMModels()
      setAsyncModels(Array.isArray(models) ? models : [])
      // Toast при частичной загрузке: один провайдер ок, другой — ошибка
      if (providerErrors?.grok && models.some((m) => m?.provider === 'openai')) {
        showToast(t('toast.llm.grokFailed', { error: providerErrors.grok }), 'error')
      }
      if (providerErrors?.openai && models.some((m) => m?.provider === 'grok')) {
        showToast(t('toast.llm.openaiFailed', { error: providerErrors.openai }), 'error')
      }
      if (providerErrors?.groq && models.some((m) => m?.provider !== 'groq')) {
        showToast(t('toast.llm.groqFailed', { error: providerErrors.groq }), 'error')
      }
      if (providerErrors?.openrouter && models.some((m) => m?.provider !== 'openrouter')) {
        showToast(t('toast.llm.openrouterFailed', { error: providerErrors.openrouter }), 'error')
      }
      if (providerErrors?.minimax && models.some((m) => m?.provider !== 'minimax')) {
        showToast(t('toast.llm.minimaxFailed', { error: providerErrors.minimax }), 'error')
      }
    } catch {
      setAsyncModels([])
    } finally {
      setLoadingModels(false)
    }
  }

  async function handleSaveAll() {
    setSaving(true)
    try {
      await updateAgent(agentKey, {
        openai_model: agentEdit?.openai_model,
        temperature: parseFloat(agentEdit?.temperature) ?? 0.5,
        top_p: parseFloat(agentEdit?.top_p) ?? 1,
        max_tokens: parseInt(agentEdit?.max_tokens, 10) || 4096,
        frequency_penalty: parseFloat(agentEdit?.frequency_penalty) ?? 0,
        presence_penalty: parseFloat(agentEdit?.presence_penalty) ?? 0,
      }, configId)
      const convKey = AGENT_CONVERSATION_MESSAGES_KEYS[agentKey]
      if (convKey) {
        const v = parseInt(conversationMessagesEdit, 10)
        if (!Number.isNaN(v) && v >= 1 && v <= 200) {
          await updateSetting(convKey, v, configId)
          setData((prev) => ({ ...prev, settings: { ...prev?.settings, [convKey]: v } }))
        }
      }
      if (agentKey === 'memory_summary') {
        const v = parseInt(runEveryNEdit, 10)
        if (!Number.isNaN(v) && v >= 1 && v <= 100) {
          await updateSetting('memory_summary_run_every_n_messages', v, configId)
          setData((prev) => ({ ...prev, settings: { ...prev?.settings, memory_summary_run_every_n_messages: v } }))
        }
      }
      await updatePrompt(agentKey, 'system', { base: baseEdit, extra: [] }, configId)
      if (!['message_analyzer', 'photo_analyzer'].includes(agentKey)) {
        await updateConditionalPrompts(agentKey, conditionalRulesEdit.map((r) => ({ ...r, role: 'system' })), configId)
        setConditionalRules(conditionalRulesEdit)
      }
      setData((prev) => ({
        ...prev,
        agents: {
          ...prev?.agents,
          [agentKey]: { ...prev?.agents?.[agentKey], ...agentEdit },
        },
      }))
      setPrompts((prev) => {
        const rest = prev.filter((p) => !(p.agent_key === agentKey && p.role === 'system'))
        return [...rest, { agent_key: agentKey, role: 'system', base: baseEdit, extra: [] }]
      })
      showToast(t('toast.settings.saved'), 'success')
    } catch (e) {
      showToast(getApiErrorMessage(e, t('toast.settings.saveFailed')), 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!isValid) {
    return (
      <div className="settings-section">
        <p className="settings-error">{t('common.error')}: {t('settings.agentNotFound')}</p>
        <Link to="..">← {t('settings.nav.agents')}</Link>
      </div>
    )
  }

  if (loading) return <p className="settings-loading">{t('common.loading')}</p>
  if (error) return <p className="settings-error">{error}</p>

  const agentLabel = t(`settings.agent.label.${agentKey}`) || agentKey

  const agentOrig = data?.agents?.[agentKey] || {}
  const sysPromptOrig = (data && prompts.find((p) => p.agent_key === agentKey && p.role === 'system'))?.base ?? ''
  const convKey = AGENT_CONVERSATION_MESSAGES_KEYS[agentKey]
  const convOrig = String(data?.settings?.[convKey] ?? '')
  const runEveryNOrig = String(data?.settings?.memory_summary_run_every_n_messages ?? 10)
  const isDirty =
    JSON.stringify(agentEdit) !== JSON.stringify(agentOrig) ||
    baseEdit !== sysPromptOrig ||
    (!['message_analyzer', 'photo_analyzer'].includes(agentKey) && JSON.stringify(conditionalRulesEdit) !== JSON.stringify(conditionalRules)) ||
    (convKey && conversationMessagesEdit !== convOrig) ||
    (agentKey === 'memory_summary' && runEveryNEdit !== runEveryNOrig)

  return (
    <SettingsFormWithPanel dirty={isDirty} saving={saving} onSave={handleSaveAll}>
      <PageSection
        title={agentLabel}
        contentClassName="settings-blocks-stack"
      >
        <SettingsBlock
          label={t('settings.agent.model')}
          description={t('settings.agent.asyncModelsHint')}
        >
          <AgentForm
            agent={agentEdit}
            onAgentChange={setAgentEdit}
            asyncModels={asyncModels}
            loadModels={loadModels}
            loadingModels={loadingModels}
            t={t}
          />
        </SettingsBlock>

        {AGENT_CONVERSATION_MESSAGES_KEYS[agentKey] && (
          <AgentConversationBlock
            settingKey={AGENT_CONVERSATION_MESSAGES_KEYS[agentKey]}
            value={conversationMessagesEdit}
            onChange={setConversationMessagesEdit}
            t={t}
          />
        )}

        {agentKey === 'memory_summary' && (
          <SettingsBlock
            label={t('settings.key.memory_summary_run_every_n_messages')}
            description={t('settings.desc.memory_summary_run_every_n_messages')}
          >
            <div className="agent-conversation-row">
              <input
                type="number"
                min="1"
                max="100"
                value={runEveryNEdit}
                onChange={(e) => setRunEveryNEdit(e.target.value)}
                className="setting-value"
              />
            </div>
          </SettingsBlock>
        )}

        <SystemPromptBlock
          base={baseEdit}
          onBaseChange={setBaseEdit}
          conditionalRules={['message_analyzer', 'photo_analyzer'].includes(agentKey) ? null : conditionalRulesEdit}
          onConditionalRulesChange={['message_analyzer', 'photo_analyzer'].includes(agentKey) ? undefined : setConditionalRulesEdit}
          l4Definitions={l4Definitions}
          t={t}
        />
        {['message_analyzer', 'photo_analyzer'].includes(agentKey) ? (
          <>
            <UserPromptInfo agentKey={agentKey} t={t} />
            <MessageAnalyzerPayloadInfo t={t} />
          </>
        ) : (
          <>
            <UserPromptInfo agentKey={agentKey} t={t} />
            {GENERATOR_OUTPUT_SCHEMAS[agentKey] && (
              <GeneratorOutputInfo agentKey={agentKey} t={t} />
            )}
          </>
        )}
      </PageSection>
    </SettingsFormWithPanel>
  )
}

function AgentConversationBlock({ settingKey, value, onChange, t }) {
  return (
    <SettingsBlock
      label={t('settings.agent.conversationMessagesCount')}
      description={t(`settings.desc.${settingKey}`)}
    >
      <div className="agent-conversation-row">
        <input
          type="number"
          min="1"
          max="200"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="setting-value"
        />
      </div>
    </SettingsBlock>
  )
}

function formatModelLabel(modelId) {
  if (!modelId) return ''
  const lower = String(modelId).toLowerCase()
  if (lower.startsWith('grok')) return `Grok ${modelId}`
  if (lower.startsWith('groq:')) return `Groq ${String(modelId).slice(5)}`
  if (lower.startsWith('openrouter:')) return `OpenRouter ${String(modelId).slice(11)}`
  if (lower.startsWith('minimax:')) return `MiniMax ${String(modelId).slice(8)}`
  return `OpenAI ${modelId}`
}

function providerFromModelId(modelId) {
  const lower = String(modelId || '').toLowerCase()
  if (lower.startsWith('grok')) return 'grok'
  if (lower.startsWith('groq:')) return 'groq'
  if (lower.startsWith('openrouter:')) return 'openrouter'
  if (lower.startsWith('minimax:')) return 'minimax'
  return 'openai'
}

function AgentForm({ agent, onAgentChange, asyncModels, loadModels, loadingModels, t }) {
  const model = agent?.openai_model || ''
  const temp = String(agent?.temperature ?? 0.5)
  const topP = String(agent?.top_p ?? 1)
  const maxTokens = String(agent?.max_tokens ?? 4096)
  const freqPenalty = String(agent?.frequency_penalty ?? 0)
  const presPenalty = String(agent?.presence_penalty ?? 0)

  const update = (updates) => onAgentChange((prev) => ({ ...prev, ...updates }))

  // Модели из API: [{id, provider, label, supported_params}]. Для выбранной модели вне списка — форматируем label.
  const modelOptions = asyncModels
  const modelInList = modelOptions.some((m) => (typeof m === 'object' ? m.id : m) === model)
  const fallbackLabel = formatModelLabel(model)
  const options = modelInList
    ? modelOptions
    : model ? [{ id: model, provider: providerFromModelId(model), label: fallbackLabel, supported_params: { temperature: true, top_p: true, max_tokens: true, frequency_penalty: true, presence_penalty: true } }, ...modelOptions] : modelOptions

  const selectedModel = options.find((m) => (typeof m === 'object' ? m.id : m) === model)
  const supportedParams = selectedModel?.supported_params ?? { temperature: true, top_p: true, max_tokens: true, frequency_penalty: true, presence_penalty: true }

  const modelSelectOptions = loadingModels
    ? [
        ...(model ? [{ value: model, label: fallbackLabel, provider: providerFromModelId(model), async_mode: false }] : []),
        { value: '__loading__', label: t('common.loading') },
      ]
    : options.map((m) => {
        const opt = typeof m === 'object' ? m : { id: m, label: m, provider: 'openai', async_mode: false }
        const rawProvider = String(opt.provider || '').toLowerCase()
        const provider = ['openai', 'grok', 'groq', 'openrouter', 'minimax'].includes(rawProvider)
          ? rawProvider
          : providerFromModelId(opt.id)
        const asyncMode = !!opt.async_mode
        const label = asyncMode ? `${opt.label} (${t('settings.agent.asyncMode')})` : opt.label
        return { value: opt.id, label, provider, async_mode: asyncMode }
      })

  return (
    <div className="agent-fields">
      <SettingsField label={t('settings.agent.model')}>
        <Select
          options={modelSelectOptions}
          value={model}
          onChange={(v) => update({ openai_model: v })}
          placeholder={t('common.notSelected')}
          dropdownPosition="down"
          onOpen={loadModels}
        />
      </SettingsField>
      {supportedParams.temperature !== false && (
        <SettingsField label={t('settings.agent.temperature')}>
          <input
            type="number"
            min="0"
            max="2"
            step="0.1"
            value={temp}
            onChange={(e) => update({ temperature: parseFloat(e.target.value) ?? 0.5 })}
          />
        </SettingsField>
      )}
      {supportedParams.top_p !== false && (
        <SettingsField label={t('settings.agent.top_p')} hint={t('settings.agent.top_p_hint')}>
          <input
            type="number"
            min="0"
            max="1"
            step="0.05"
            value={topP}
            onChange={(e) => update({ top_p: parseFloat(e.target.value) ?? 1 })}
          />
        </SettingsField>
      )}
      {supportedParams.max_tokens !== false && (
        <SettingsField label={t('settings.agent.max_tokens')} hint={t('settings.agent.max_tokens_hint')}>
          <input
            type="number"
            min="1"
            max="128000"
            step="1"
            value={maxTokens}
            onChange={(e) => update({ max_tokens: parseInt(e.target.value, 10) || 4096 })}
          />
        </SettingsField>
      )}
      {supportedParams.frequency_penalty !== false && (
        <SettingsField label={t('settings.agent.frequency_penalty')} hint={t('settings.agent.frequency_penalty_hint')}>
          <input
            type="number"
            min="-2"
            max="2"
            step="0.1"
            value={freqPenalty}
            onChange={(e) => update({ frequency_penalty: parseFloat(e.target.value) ?? 0 })}
          />
        </SettingsField>
      )}
      {supportedParams.presence_penalty !== false && (
        <SettingsField label={t('settings.agent.presence_penalty')} hint={t('settings.agent.presence_penalty_hint')}>
          <input
            type="number"
            min="-2"
            max="2"
            step="0.1"
            value={presPenalty}
            onChange={(e) => update({ presence_penalty: parseFloat(e.target.value) ?? 0 })}
          />
        </SettingsField>
      )}
    </div>
  )
}

function SystemPromptBlock({ base, onBaseChange, conditionalRules, onConditionalRulesChange, l4Definitions = [], t }) {
  return (
    <SettingsBlock
      label={t('settings.prompt.roleSystem')}
      description={t('settings.promptsHint')}
    >
      <div className="settings-block-inner">
        <SettingsBlock label={t('settings.prompt.baseLabel')} nested>
          <textarea
            value={base || ''}
            onChange={(e) => onBaseChange(e.target.value)}
            className="prompt-textarea"
            rows={10}
            placeholder={t('settings.prompt.placeholder')}
          />
        </SettingsBlock>
        {conditionalRules && onConditionalRulesChange && (
          <SettingsBlock
            label={t('settings.conditionalPrompts.title')}
            description={t('settings.conditionalPrompts.hint')}
            nested
          >
            <ConditionalPromptsList
              rules={conditionalRules}
              onRulesChange={onConditionalRulesChange}
              l4Definitions={l4Definitions}
              t={t}
            />
          </SettingsBlock>
        )}
      </div>
    </SettingsBlock>
  )
}

function translatePayloadStructure(schema, t) {
  const out = {}
  for (const [key, val] of Object.entries(schema)) {
    if (key.startsWith('_')) continue
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const descKey = val._desc
      const desc = descKey ? t(descKey) : t('settings.prompt.payload.object')
      const inner = { ...val }
      delete inner._desc
      out[key] = Object.keys(inner).length > 0
        ? { description: desc, fields: translatePayloadStructure(inner, t) }
        : desc
    } else {
      out[key] = typeof val === 'string' ? t(val) : val
    }
  }
  return out
}

function MessageAnalyzerPayloadInfo({ t }) {
  return (
    <SettingsBlock
      label={t('settings.prompt.payload.ma.responseFormatTitle')}
      description={t('settings.prompt.payload.ma.responseFormatDesc')}
    >
      <StructuredDataView value={MESSAGE_ANALYZER_OUTPUT_SCHEMA_AS_OBJECT} compact className="prompt-payload-structure" />
    </SettingsBlock>
  )
}

function UserPromptInfo({ agentKey, t }) {
  const schema = USER_PROMPT_PAYLOAD_SCHEMAS[agentKey]
  if (!schema) return null

  const structure = translatePayloadStructure(schema, t)

  return (
    <SettingsBlock
      label={t('settings.prompt.roleUser')}
      description={t('settings.prompt.userReadonlyHint')}
    >
      <StructuredDataView value={structure} compact className="prompt-payload-structure" />
    </SettingsBlock>
  )
}

function GeneratorOutputInfo({ agentKey, t }) {
  const schema = GENERATOR_OUTPUT_SCHEMAS[agentKey]
  if (!schema) return null

  const titleKey = `settings.prompt.payload.generator.outputFormatTitle`
  const descKey = `settings.prompt.payload.generator.outputFormatDesc`

  return (
    <SettingsBlock
      label={t(titleKey)}
      description={t(descKey)}
    >
      <StructuredDataView value={schema} compact className="prompt-payload-structure" />
    </SettingsBlock>
  )
}

function ConditionalPromptsList({ rules, onRulesChange, l4Definitions = [], t }) {
  const [deleteRuleIdx, setDeleteRuleIdx] = useState(null)
  const l4Keys = l4Definitions.map((d) => d.key).filter(Boolean)
  const paramKeys = [...new Set([...L2_VAR_KEYS, ...L3_KEYS, ...l4Keys])]
  function addRule() {
    const newRule = {
      condition: { param: 'spending_intent_score', op: 'gte', value: 0 },
      prompt_text: '',
      role: 'system',
      order: rules.length,
    }
    onRulesChange([...rules, newRule])
  }

  function updateRule(idx, updates) {
    const next = [...rules]
    next[idx] = { ...next[idx], ...updates }
    onRulesChange(next)
  }

  function removeRule(idx) {
    onRulesChange(rules.filter((_, i) => i !== idx))
    setDeleteRuleIdx(null)
  }

  return (
    <div className="conditional-prompts-list">
      {rules.map((rule, idx) => (
        <SettingsItemCard
          key={idx}
          label={`#${idx + 1}`}
          onDelete={() => setDeleteRuleIdx(idx)}
          deleteTitle={t('common.delete')}
          className="conditional-prompt-rule"
        >
          <div className="conditional-rule-condition">
            <SettingsField label={t('settings.conditionalPrompts.conditionLabel')} labelWraps={false}>
              <ConditionBuilder
                condition={rule.condition}
                onChange={(c) => updateRule(idx, { ...rule, condition: c })}
                paramKeys={paramKeys}
                l4Keys={l4Keys}
                l4Definitions={l4Definitions}
                confirmRemove
              />
            </SettingsField>
          </div>
          <div className="conditional-rule-prompt">
            <SettingsField label={t('settings.conditionalPrompts.promptLabel')}>
              <textarea
                value={rule.prompt_text || ''}
                onChange={(e) => updateRule(idx, { ...rule, prompt_text: e.target.value })}
                rows={4}
                placeholder={t('settings.conditionalPrompts.promptPlaceholder')}
              />
            </SettingsField>
          </div>
          <div className="conditional-rule-meta">
            <SettingsField label={t('settings.conditionalPrompts.orderLabel')}>
              <input
                type="number"
                min={0}
                value={rule.order ?? idx}
                onChange={(e) => updateRule(idx, { ...rule, order: parseInt(e.target.value, 10) || 0 })}
                className="conditional-rule-order-input"
              />
            </SettingsField>
          </div>
        </SettingsItemCard>
      ))}
      <div className="conditional-prompts-actions">
        <button type="button" className="btn btn-secondary" onClick={addRule}>
          {t('settings.conditionalPrompts.addRule')}
        </button>
      </div>
      <ConfirmModal
        open={deleteRuleIdx != null}
        title={t('settings.deleteConditionalPromptTitle')}
        message={t('settings.deleteConditionalPromptMessage')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => deleteRuleIdx != null && removeRule(deleteRuleIdx)}
        onCancel={() => setDeleteRuleIdx(null)}
        danger
      />
    </div>
  )
}
